import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import ConfirmModal from '../common/ConfirmModal';
import { RequiredLabel } from "../../utils/validators";
import {
    getItem,
    startProcessWithStep,           // - startProcessWithStep(itemId, { stepTo, quantity, date, notes })
    advanceProcessStep,             // - advanceProcessStep(itemId, { stepFrom, stepTo, quantity, date, notes })
    finishProcessWithConversion,    // - finishProcessWithConversion(itemId, { stepFrom, consumeQty, finalName, finalUnit, finalQty, destLocationId, date, notes })
    assignFolios,
} from "../../services/Items/Inventory";
import { getLocations } from "../../services/Configs/LocationsService";
import { getCategories } from '../../services/Configs/ItemCategoriesService';
import { getFolios, getFoliosByQuery } from '../../services/Configs/ItemFolios';
import { getSupplies } from '../../services/Supplies/Supplies';

function InfoNote({ title = "Ayuda", children }) {
    return (
        <Alert variant="info" className="py-2 px-3 mb-2">
            <span className="badge text-bg-primary me-2">Guía</span>
            <strong>{title}:</strong>
            <div className="mt-1 small mb-0">{children}</div>
        </Alert>
    );
};

const STEP_OPTIONS = [
    { value: "COCCION", label: "Cocción" },
    { value: "MOLIENDA", label: "Molienda" },
    { value: "FERMENTACION", label: "Fermentación" },
    { value: "DESTILACION", label: "Destilación" },
    { value: "REPOSO", label: "Reposo" },
    { value: "ENVASADO", label: "Envasado" },
    { value: "ETIQUETADO", label: "Etiquetado" },
];

const RAW_FIXED_STEP = "DESTILACION";
const RAW_FIXED_TARGET_UNIT = "Litros";
const FINISHED_CATEGORY_NAME = "Producto Terminado";

const findCategoryIdByName = (rows = [], target = "") => {
    const normalizedTarget = String(target).trim().toLowerCase();
    const found = rows.find(
        (c) => String(c.name || "").trim().toLowerCase() === normalizedTarget
    );
    return found?.id ?? 0;
};

const UNIT_OPTIONS = [
    { value: "Kilogramos", label: "Kilogramo" },
    { value: "Gramos", label: "Gramo" },
    { value: "Litros", label: "Litro" },
    { value: "Mililitros", label: "Mililitro" },
    { value: "Botellas", label: "Botella" },
    { value: "Cajas", label: "Caja (12 botellas)" },
    { value: "Piezas", label: "Pieza" },
    { value: "onzas", label: "Onzas" },
];

const getUserId = () => sessionStorage.getItem('userId') ?? 0;

export default function ProcessModal({
    show,
    onClose,
    itemId,
    currentStage,        // 'raw' | 'processing' | 'finished'
    onSuccess,
    locations = [],       // [{id, name}]
    forceProcessingForm = false,
}) {
    const userId = getUserId();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [okMsg, setOkMsg] = useState("");
    const [item, setItem] = useState(null);
    const [data, setData] = useState({});
    const [categories, setCategories] = useState([]);
    const [folios, setFolios] = useState([]);
    const [folioMode, setFolioMode] = useState('auto');        // 'auto' | 'manual'
    const [manualType, setManualType] = useState('single');    // 'single' | 'range'
    const [selectedLocations, setSelectedLocations] = useState([]);
    // Acción cuando estás en processing
    // 'advance' = avanzar sub-etapa, 'finish' = embotellar
    const [processingAction, setProcessingAction] = useState("advance");

    const [form, setForm] = useState({
        // comunes
        date: new Date().toISOString().slice(0, 10),
        notes: '',
        userId: getUserId(),
        categoryId: 0,
        overrideName: '',
        overrideDescription: '',
        // iniciar / avanzar
        quantity: '',               // cantidad a consumir (siempre en la unidad base del item actual)
        stepTo: '',                 // sub-etapa destino
        // finish
        finalName: '',
        finalDescription: '',
        finalPrice: '',
        finalUnit: "Botellas",
        destLocationId: '',         // ubicación de producto terminado
        // avanzar
        advanceTargetUnit: '',     // unidad destino (opcional)
        advanceMode: "direct",     // "direct" | "factor"
        advanceToQuantity: '',     // si advanceMode === "direct"
        advanceFactor: '',         // si advanceMode === "factor"
        // embotellado
        presentation: "BOTELLA_750", // "BOTELLA_750" | "CAJA_12"
        finalCount: '',             // <— número ingresado por el usuario
        folioId: '',           // individual
        startFolioId: '',      // rango
        endFolioId: '',        // rango (lo calculamos/mostramos; el back valida)
        folioSearch: '',
    });

    // Confirm Modal
    const [showBottleCalc, setShowBottleCalc] = useState(false);
    const [bottlesGoal, setBottlesGoal] = useState("");

    // Consumibles
    const [supplies, setSupplies] = useState([]);
    const [selectedSupplyIds, setSelectedSupplyIds] = useState([]);

    const isRaw = currentStage === "raw";
    const isProcessing = currentStage === "processing";
    const showProcessingUI = forceProcessingForm || isProcessing;
    const isStarting = isRaw && forceProcessingForm;
    const isFinished = currentStage === "finished";

    // helpers equivalencias
    const litersPerBottle = 0.75;                           // 750 ml por botella
    const bottlesPerBox = 12;                               // 12 botellas por caja
    const litersPerBox = bottlesPerBox * litersPerBottle;   // 9 L por caja

    const finalCountNum = Number(form.finalCount || 0);
    const isSinglePiece = finalCountNum === 1;
    const isMultiplePieces = finalCountNum > 1;

    useEffect(() => {
        if (!show || !itemId) return;
        let mounted = true;
        setLoading(true);
        setError(""); setOkMsg("");

        getItem(itemId)
            .then((it) => {
                if (!mounted) return;
                setItem(it || null);

                setForm((f) => ({
                    ...f,
                    categoryId: it?.categoryId ?? "",
                    stepTo: currentStage === "raw" ? RAW_FIXED_STEP : "",
                    advanceTargetUnit: currentStage === "raw" ? RAW_FIXED_TARGET_UNIT : "",
                }));
            })
            .catch(() => setError("No se pudo cargar la información del ítem."))
            .finally(() => setLoading(false));

        return () => { mounted = false; };
    }, [show, itemId]);

    // Resetea el form al abrir/cambiar etapa
    useEffect(() => {
        if (!show) return;

        setForm((f) => ({
            ...f,
            quantity: "",
            stepTo: currentStage === "raw" ? RAW_FIXED_STEP : "",
            finalName: "",
            finalDescription: "",
            finalPrice: "",
            finalUnit: "Botellas",
            destLocationId: "",
            date: new Date().toISOString().slice(0, 10),
            notes: "",
            advanceTargetUnit: currentStage === "raw" ? RAW_FIXED_TARGET_UNIT : "",
            advanceMode: "direct",
            advanceToQuantity: "",
            advanceFactor: "",
            presentation: "BOTELLA_750",
            finalCount: "",
            folioId: "",
            startFolioId: "",
            endFolioId: "",
            folioSearch: "",
        }));

        setFolioMode("auto");
        setManualType("single");
        setProcessingAction(currentStage === "processing" ? "finish" : "advance");
        setError("");
        setOkMsg("");
    }, [show, currentStage]);

    useEffect(() => {
        const asArray = (x) => {
            if (Array.isArray(x)) return x;
            if (x && Array.isArray(x.data)) return x.data;
            if (x && Array.isArray(x.result)) return x.result;
            if (x && Array.isArray(x.items)) return x.items;
            return [];
        };

        getCategories().then((list) => {
            const cats = Array.isArray(list) ? list : [];
            setCategories(cats);

            if (currentStage === "raw") {
                const processingCategoryId = findCategoryIdByName(cats, "Producción en Proceso");

                setForm((f) => ({
                    ...f,
                    categoryId: processingCategoryId || f.categoryId,
                }));
            }

            if (currentStage === "processing") {
                const finishedCategoryId = findCategoryIdByName(cats, FINISHED_CATEGORY_NAME);

                setForm((f) => ({
                    ...f,
                    categoryId: finishedCategoryId || f.categoryId,
                }));
            }
        });

        getLocations()
            .then((lList) => {
                const locs = asArray(lList);
                setSelectedLocations(locs.map((l) => l.id));
            })
            .catch((err) => console.error("Error cargando ubicaciones:", err));

        getFolios(1, 100, true)
            .then((resp) => {
                const items = resp?.items || [];
                setFolios(items);
            })
            .catch((err) => console.error("Error cargando folios:", err));

        getSupplies().then((rows) => {
            setSupplies(Array.isArray(rows) ? rows : []);
        });
    }, []);

    //Final count
    useEffect(() => {
        const count = Number(form.finalCount || 0);

        if (!count || count <= 0) return;

        if (count === 1) {
            setFolioMode("manual");
            setManualType("single");

            setForm((f) => ({
                ...f,
                startFolioId: "",
                endFolioId: "",
            }));
            return;
        }

        if (count > 1) {
            if (folioMode === "manual") {
                setManualType("range");
            }

            setForm((f) => ({
                ...f,
                folioId: "",
            }));
        }
    }, [form.finalCount, folioMode]);

    const availableQty = Number(item?.quantity ?? 0);
    const currentStep = item?.process_step ?? null;

    const stepOptionsForAdvance = useMemo(() => { return STEP_OPTIONS.filter(s => s.value !== currentStep); }, [currentStep]);

    const approxBottlesByLiters = useMemo(() => {
        if (!Number.isFinite(availableQty) || availableQty <= 0) return 0;
        return Math.floor(availableQty / litersPerBottle); // 0.75 L por botella
    }, [availableQty]);

    const approxBoxesByLiters = useMemo(() => {
        if (!Number.isFinite(availableQty) || availableQty <= 0) return 0;
        return Math.floor(availableQty / litersPerBox); // 9 L por caja (12×0.75)
    }, [availableQty]);

    const consumeNeeded = useMemo(() => {
        const count = Number(form.finalCount);
        if (!count || count <= 0) return 0;
        return form.presentation === "BOTELLA_750"
            ? count * litersPerBottle   // 0.75 L por botella
            : count * litersPerBox;     // 9 L por caja (12×0.75)
    }, [form.finalCount, form.presentation]);

    const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('es-MX') : n ?? '');

    // ---- Validaciones
    const validateCommonConsume = () => {
        const q = Number(form.quantity);
        if (Number.isNaN(q) || q <= 0) return "La cantidad debe ser mayor a 0.";
        if (q > availableQty) return "La cantidad excede el inventario disponible en la etapa actual.";
        return null;
    };

    const validateAdvance = () => {
        if (isRaw) {
            if (form.stepTo !== RAW_FIXED_STEP) {
                return "La sub-etapa inicial debe ser Destilación.";
            }

            if (form.advanceTargetUnit !== RAW_FIXED_TARGET_UNIT) {
                return "La unidad destino para materia prima debe ser Litros.";
            }

            if (!form.categoryId) {
                return "No se encontró la categoría destino 'Producción en Proceso'.";
            }
        } else {
            if (!form.stepTo) return "Selecciona la sub-etapa destino.";
            if (!currentStep) return "El ítem no tiene sub-etapa actual definida.";
        }

        if (form.advanceMode === "direct") {
            const toQ = Number(form.advanceToQuantity);
            if (!toQ || toQ <= 0) return "Ingresa una cantidad destino válida.";
        } else {
            const f = Number(form.advanceFactor);
            if (!f || f <= 0) return "Ingresa un factor de conversión válido.";
        }

        return validateCommonConsume();
    };

    const mapPresentationToUnit = (p) => p === "CAJA_12" ? "Cajas" : "Botellas";

    const validateFinish = () => {
        if (!currentStep) return "El ítem no tiene sub-etapa actual definida.";
        if (!form.finalName?.trim()) return "Captura el nombre del producto final.";
        if (!form.finalDescription?.trim()) return "Captura la descripción del producto final.";
        if (Number(form.finalPrice) <= 0) return "Captura un precio final válido.";
        if (!form.destLocationId) return "Selecciona la ubicación destino.";

        const n = Number(form.finalCount);
        if (!Number.isInteger(n) || n <= 0) return "Cantidad final inválida.";

        const need = consumeNeeded;
        if (need > availableQty) return "La cantidad excede los litros disponibles.";
        if (!form.categoryId) return "Selecciona la categoría.";

        return null;
    };

    const validateFinishFolios = () => {
        const count = Number(form.finalCount || 0);

        if (!count || count <= 0) {
            return "Ingresa una cantidad válida de piezas.";
        }

        if (count === 1) {
            if (!form.folioId) {
                return "Debes seleccionar un folio individual.";
            }
            return null;
        }

        if (count > 1) {
            if (folioMode === "auto") {
                return null;
            }

            if (folioMode === "manual") {
                if (!form.startFolioId || !form.endFolioId) {
                    return "Debes seleccionar un rango de folios.";
                }
                return null;
            }

            return "Selecciona un modo válido de asignación de folios.";
        }

        return null;
    };

    const saveAdvance = async () => {
        const err = validateAdvance();
        if (err) throw new Error(err);

        const payload = {
            stepFrom: currentStep,
            stepTo: form.stepTo,
            quantity: Number(form.quantity),
            date: form.date,
            notes: form.notes,
            advanceTargetUnit: form.advanceTargetUnit || null,
            advanceUseFactor: form.advanceMode === "factor",
            advanceFactor: form.advanceMode === "factor" ? Number(form.advanceFactor) : null,
            advanceToQuantity: form.advanceMode === "direct" ? Number(form.advanceToQuantity) : null,
            advanceOverrideName: form.overrideName || null,
            advanceOverrideDescription: form.overrideDescription || null,
            categoryId: Number(form.categoryId) || null,
        };

        const r = await advanceProcessStep(itemId, payload);
        setOkMsg("Sub-etapa avanzada correctamente.");
        onSuccess?.(r?.data?.toItemId ?? r?.toItemId ?? itemId);
    };

    const saveFinish = async () => {
        const err = validateFinish();
        if (err) throw new Error(err);

        const folioErr = validateFinishFolios();
        if (folioErr) throw new Error(folioErr);

        const finalUnit = mapPresentationToUnit(form.presentation);
        const finalQty = Number(form.finalCount);

        const r = await finishProcessWithConversion(itemId, {
            stepFrom: currentStep,
            consumeQty: consumeNeeded,            // litros calculados (0.75 por botella / 9 por caja)
            finalName: form.finalName.trim(),
            finalDescription: form.finalDescription.trim(),
            finalPrice: Number(form.finalPrice),
            finalUnit,
            finalQty,
            destLocationId: Number(form.destLocationId),
            date: form.date,
            notes: form.notes,
            categoryId: Number(form.categoryId) || null,
            supplyIds: selectedSupplyIds,
        });

        const toItemId = r?.toItemId ?? r?.data?.toItemId;

        // --- Asignación de folios (opcional según UI seleccionada)
        if (finalQty > 0) {
            if (finalQty === 1) {
                if (!form.folioId) throw new Error("Selecciona el folio individual.");

                await assignFolios(toItemId, {
                    mode: 'manual',
                    units: form.presentation,        // "BOTELLA_750" | "CAJA_12"
                    count: 1,
                    startFolioId: Number(form.folioId),
                    endFolioId: Number(form.folioId),
                });
            } else {
                if (folioMode === 'auto') {
                    await assignFolios(toItemId, {
                        mode: 'auto',
                        units: form.presentation,
                        count: finalQty,
                    });
                } else {
                    if (!form.startFolioId || !form.endFolioId) {
                        throw new Error("Selecciona el rango de folios.");
                    }

                    await assignFolios(toItemId, {
                        mode: 'manual',
                        units: form.presentation,
                        count: finalQty,
                        startFolioId: Number(form.startFolioId),
                        endFolioId: Number(form.endFolioId),
                    });
                }
            }
        }

        setOkMsg("Proceso finalizado y folios asignados.");
        onSuccess?.(toItemId);
    };

    const handleSave = async () => {
        try {
            setError("");
            setOkMsg("");
            setSaving(true);

            if (isStarting) {
                const err = validateAdvance();
                if (err) throw new Error(err);

                const r1 = await startProcessWithStep(itemId, {
                    quantity: Number(form.quantity),
                    date: form.date,
                    notes: form.notes,
                    overrideName: form.overrideName || null,
                    overrideDescription: form.overrideDescription || null,
                });

                const toItemId =
                    r1?.data?.toItemId ?? r1?.toItemId ?? itemId;

                const wantsConversion =
                    !!form.advanceTargetUnit ||
                    (form.advanceMode === "direct" ? !!Number(form.advanceToQuantity) : !!Number(form.advanceFactor));

                if (wantsConversion) {
                    await advanceProcessStep(toItemId, {
                        stepFrom: form.stepTo,
                        stepTo: form.stepTo,
                        quantity: Number(form.quantity),
                        date: form.date,
                        notes: form.notes,
                        advanceTargetUnit: form.advanceTargetUnit || null,
                        advanceUseFactor: form.advanceMode === "factor",
                        advanceFactor: form.advanceMode === "factor" ? Number(form.advanceFactor) : null,
                        advanceToQuantity: form.advanceMode === "direct" ? Number(form.advanceToQuantity) : null,
                        advanceOverrideName: form.overrideName || null,
                        advanceOverrideDescription: form.overrideDescription || null,
                        categoryId: Number(form.categoryId) || null,
                    });
                }

                setOkMsg("Proceso iniciado correctamente.");
                onSuccess?.(toItemId);
                return;
            }

            if (showProcessingUI) {
                if (processingAction === "advance") {
                    await saveAdvance();
                    return;
                }

                if (processingAction === "finish") {
                    await saveFinish();
                    return;
                }
            }
        } catch (e) {
            console.error(e);
            setError(e?.message || "No se pudo guardar el movimiento.");
        } finally {
            setSaving(false);
        }
    };

    // ---- Título
    const title = isRaw
        ? "Conversión de materia prima a litros"
        : isProcessing
            ? "Envasado final a producto terminado"
            : "Acción no disponible";

    // ---- UI helpers
    const renderHeaderAlert = () => (
        item && (
            <Alert variant="secondary" className="py-2">
                <div><strong>Producto:</strong> {item.name}</div>
                <div><strong>Etapa actual:</strong> {currentStage}{currentStep ? ` / ${currentStep}` : ""}</div>
                <div><strong>Cantidad disponible en esta etapa:</strong> {fmt(availableQty)}</div>
                <div><strong>Unidad base:</strong> {item.unitOfWeightMeasurement || "N/D"}</div>
            </Alert>
        )
    );

    return (
        <Modal show={show} onHide={onClose} size="lg" backdrop="static" keyboard={false} dialogClassName="process-modal" contentClassName="process-modal-content">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <style>{`
                .modal-dialog.process-modal .modal-content.process-modal-content .tight-row { margin-bottom: .5rem; }
                .modal-dialog.process-modal .modal-content.process-modal-content .tight-row > [class^="col"] { padding-bottom: 0; }
                .modal-dialog.process-modal .modal-content.process-modal-content .form-label { margin-bottom: .25rem; }
                .modal-dialog.process-modal .modal-content.process-modal-content .form-control,
                .modal-dialog.process-modal .modal-content.process-modal-content .form-select {
                    padding: .35rem .5rem; height: calc(1.5em + .75rem + 2px);
                }
                .modal-dialog.process-modal .modal-content.process-modal-content .alert-secondary { padding: .5rem .75rem; margin-bottom: .5rem; }
                .modal-dialog.process-modal .modal-content.process-modal-content .alert-info { padding: .5rem .75rem; margin-bottom: .5rem; }
                .modal-dialog.process-modal .modal-content.process-modal-content .alert-info .small { line-height: 1.25; }
                `}
            </style>
            <Modal.Body>
                {loading ? (
                    <div className="text-center my-4"><Spinner animation="border" /></div>
                ) : (
                    <>
                        {renderHeaderAlert()}
                        {isRaw && (
                            <Form>
                                {isStarting && (
                                    <Row className="mb-3">
                                        {/* Nombre destino */}
                                        <Col md={6}>
                                            <Form.Group className="mt-2">
                                                <Form.Label>Nombre destino (opcional)</Form.Label>
                                                <Form.Control value={form.overrideName}
                                                    onChange={(e) => setForm(f => ({ ...f, overrideName: e.target.value }))} />
                                            </Form.Group>
                                        </Col>

                                        {/* Descripción */}
                                        <Col md={6}>
                                            <Form.Group className="mt-2">
                                                <Form.Label>Descripción (opcional)</Form.Label>
                                                <Form.Control value={form.overrideDescription}
                                                    onChange={(e) => setForm(f => ({ ...f, overrideDescription: e.target.value }))} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                {/* Unidad destino */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        {!isStarting && (
                                            <Form.Group className="mt-2">
                                                <Form.Label>Unidad destino (opcional)</Form.Label>
                                                <Form.Select
                                                    value={form.overrideUnit}
                                                    onChange={(e) => setForm(f => ({ ...f, overrideUnit: e.target.value }))}>
                                                    <option value="">(igual que origen)</option>
                                                    <option value="Kilogramo">Kilogramos</option>
                                                    <option value="Litro">Litros</option>
                                                    <option value="Pieza">Piezas</option>
                                                    <option value="Gramo">Gramos</option>
                                                    <option value="Mililitro">Mililitros</option>
                                                    <option value="Onzas">Onzas</option>
                                                </Form.Select>
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                            </Form>
                        )}

                        {showProcessingUI && (
                            <Form>
                                {/* Nombre-Descripción */}
                                {processingAction === "advance" && !isStarting && (
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group className="mt-2">
                                                <Form.Label>Nombre destino (opcional)</Form.Label>
                                                <Form.Control
                                                    value={form.overrideName}
                                                    onChange={(e) => setForm(f => ({ ...f, overrideName: e.target.value }))}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mt-2">
                                                <Form.Label>Descripción (opcional)</Form.Label>
                                                <Form.Control
                                                    value={form.overrideDescription}
                                                    onChange={(e) => setForm(f => ({ ...f, overrideDescription: e.target.value }))}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                {/* Acción */}
                                {isRaw && (
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Label>Acción</Form.Label>
                                            <div className="d-flex gap-3 align-items-center">
                                                <Form.Check
                                                    type="radio"
                                                    label="Conversión a litros"
                                                    name="procAction"
                                                    id="proc-adv"
                                                    checked={processingAction === "advance"}
                                                    readOnly
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                {/* Avanzar sub-etapa */}
                                {(processingAction === "advance") && (
                                    <>
                                        <Row className="tight-row">
                                            {/* Sub-etapa destino */}
                                            <Col md={6}>
                                                <Form.Label>Sub-etapa destino</Form.Label>
                                                {isRaw ? (
                                                    <Form.Control value="Destilación" disabled readOnly />
                                                ) : (
                                                    <Form.Select
                                                        value={form.stepTo}
                                                        onChange={e => setForm({ ...form, stepTo: e.target.value })}
                                                    >
                                                        <option value="">Selecciona…</option>
                                                        {stepOptionsForAdvance.map(o => (
                                                            <option key={o.value} value={o.value}>{o.label}</option>
                                                        ))}
                                                    </Form.Select>
                                                )}
                                            </Col>

                                            {/* Cantidad a mover */}
                                            <Col md={6}>
                                                <Form.Label>Cantidad a mover</Form.Label>
                                                <Form.Control
                                                    type="number" min="0" step="any"
                                                    value={form.quantity}
                                                    onChange={e => setForm({ ...form, quantity: e.target.value })}
                                                    placeholder="0"
                                                />
                                            </Col>
                                        </Row>

                                        <Row className="tight-row">
                                            {/* Unidad destino */}
                                            <Col md={6}>
                                                <Form.Label>Unidad destino</Form.Label>
                                                {isRaw ? (
                                                    <Form.Control value="Litros" disabled readOnly />
                                                ) : (
                                                    <Form.Select
                                                        value={form.advanceTargetUnit}
                                                        onChange={e => setForm({ ...form, advanceTargetUnit: e.target.value })}
                                                    >
                                                        <option value="">— misma unidad —</option>
                                                        {UNIT_OPTIONS.map(u => (
                                                            <option key={u.value} value={u.value}>{u.label}</option>
                                                        ))}
                                                    </Form.Select>
                                                )}
                                            </Col>

                                            {/* Categoría */}
                                            <Col xl={6}>
                                                <Form.Group>
                                                    <Form.Label>Categoría *:</Form.Label>
                                                    {isRaw ? (
                                                        <Form.Control value="Producción en Proceso" disabled readOnly />
                                                    ) : (
                                                        <Form.Select
                                                            name="categoryId"
                                                            value={form.categoryId ?? ""}
                                                            onChange={(e) => setForm(f => ({ ...f, categoryId: Number(e.target.value) }))}
                                                        >
                                                            <option value="">Seleccionar categoría</option>
                                                            {categories.map(c => (
                                                                <option key={`cat-${c.id}`} value={c.id}>{c.name}</option>
                                                            ))}
                                                        </Form.Select>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Guía */}
                                        <Row className="mb-2">
                                            <Col md={12}>
                                                <InfoNote title="Cómo usar el factor de conversión">
                                                    <div>La fórmula es <b>Destino = Origen × Factor</b>.</div>
                                                    <ul className="mb-1">
                                                        <li>El <b>Factor</b> es “cuánta <i>unidad destino</i> obtienes por <b>1</b> unidad de origen”.</li>
                                                        <li>Ejemplos:</li>
                                                        <ul>
                                                            <li><b>KG → L:</b> factor = <b>litros_producidos ÷ kilos_entrada</b>.</li>
                                                            <li><b>KG → botellas:</b> factor = <b> botellas_producidas ÷ kilos_entrada</b>.</li>
                                                            <li><b>KG → cajas (12 botellas):</b> factor = <b>(cajas × 12) ÷ kilos_entrada</b>.</li>
                                                            <li><b>L → botellas:</b> factor =<b> botellas ÷ litros_entrada (= 1/0.75 ≈ 1.333 si cada botella es 0.75 L).</b>.</li>
                                                        </ul>
                                                        <li>Ejemplo gráfico:</li>
                                                        <ul>
                                                            <li>litros introducidos → (840 <b>botellas</b> x 0.75 <b>litros</b>)/ botellas = 630 <b>litros</b>.</li>
                                                            <li>Factor de conversión KG → L: factor = 630 <b>litros</b> / 11,840<b>kilogramos</b> = <b>0.0532094595 lts/kgs</b>.</li>
                                                            <ul>(inverso: <b>~18.79365 kg</b> por cada <b>1 L</b> producido).</ul>
                                                        </ul>
                                                        <li>Chequeo →</li>
                                                        <ul>
                                                            <li>11,840 kg x 0.0532094595 = 630 litros</li>
                                                            <li>y 630 lts ÷ 0.75 = 840 botellas</li>
                                                        </ul>
                                                        <br></br>
                                                        <li>Si no conoces el factor, usa “<b>Ingresar cantidad</b>” y escribe la salida directamente en la unidad destino.</li>
                                                    </ul>
                                                </InfoNote>
                                            </Col>
                                        </Row>

                                        {/* Calcular cantidad */}
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => {
                                                const kg = Number(form.quantity);
                                                if (!kg || kg <= 0) {
                                                    toast.warning("Primero captura la cantidad origen (kg).");
                                                    return;
                                                }
                                                setBottlesGoal(String(approxBottlesByLiters || ""));
                                                setShowBottleCalc(true);
                                            }}
                                        >
                                            Calcular factor desde botellas…
                                        </Button>

                                        {/* Método para cantidad de destino */}
                                        <Row className="tight-row">
                                            <Col md={12}>
                                                <Form.Label>Método para cantidad destino</Form.Label>
                                                <div className="d-flex gap-3 align-items-center">
                                                    <Form.Check
                                                        type="radio" label="Ingresar cantidad"
                                                        name="advMode" checked={form.advanceMode === "direct"}
                                                        onChange={() => setForm({ ...form, advanceMode: "direct" })}
                                                    />
                                                    <Form.Check
                                                        type="radio" label="Usar factor de conversión"
                                                        name="advMode" checked={form.advanceMode === "factor"}
                                                        onChange={() => setForm({ ...form, advanceMode: "factor" })}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Calculo directo | calculo indirecto */}
                                        {form.advanceMode === "direct" ? (
                                            <Row className="tight-row">
                                                <Col md={6}>
                                                    <Form.Label>Cantidad destino</Form.Label>
                                                    <Form.Control
                                                        type="number" min="0" step="any"
                                                        value={form.advanceToQuantity}
                                                        onChange={e => setForm({ ...form, advanceToQuantity: e.target.value })}
                                                        placeholder="0"
                                                    />
                                                    <Form.Text>Se sumará en la unidad destino seleccionada.</Form.Text>

                                                </Col>
                                                <Col md={6}>
                                                    <InfoNote title="Ingresar cantidad">
                                                        Escribe directamente la <b>cantidad en la unidad destino</b> (por ejemplo, si elegiste “Litro” y mediste 3.5 L, captura <b>3.5</b>).
                                                    </InfoNote>
                                                </Col>
                                            </Row>
                                        ) : (
                                            <Row className="mb-2">
                                                <Col md={6}>
                                                    <Form.Label>Factor de conversión</Form.Label>
                                                    <Form.Control
                                                        type="number" min="0" step="any"
                                                        value={form.advanceFactor}
                                                        onChange={e => setForm({ ...form, advanceFactor: e.target.value })}
                                                        placeholder="Ej.: 0.7  (Litros por 1 Kg)"
                                                    />
                                                    <Form.Text>Destino = Origen × Factor</Form.Text>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Label>Destino calculado</Form.Label>
                                                    <Form.Control
                                                        readOnly
                                                        value={
                                                            form.advanceMode === "factor"
                                                                ? String((Number(form.quantity) || 0) * (Number(form.advanceFactor) || 0) || "")
                                                                : ""
                                                        }
                                                        placeholder="Se calcula automáticamente"
                                                    />
                                                    <Form.Text>Se calcula automáticamente a partir de Origen y Factor.</Form.Text>
                                                </Col>
                                            </Row>
                                        )}

                                        {/* Fecha del movimiento | Notas */}
                                        <Row className="mb-2">
                                            <Col md={6}>
                                                <Form.Label>Fecha del movimiento</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={form.date}
                                                    onChange={e => setForm({ ...form, date: e.target.value })}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Form.Label>Notas (opcional)</Form.Label>
                                                <Form.Control
                                                    as="textarea" rows={1}
                                                    value={form.notes}
                                                    onChange={e => setForm({ ...form, notes: e.target.value })}
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                )}

                                {/* Finalizar (embotellado) */}
                                {processingAction === "finish" && (
                                    <>
                                        {/* Instrucciones */}
                                        <Row className="mb-2">
                                            <Col md={12}>
                                                <InfoNote title="Envasado Final">
                                                    <div>El producto final se presenta como <b>botella</b> o <b>caja</b>.</div>
                                                    <ul className="mb-1">
                                                        <li>Cada botella contiene <b>0.75 Lt</b>.</li>
                                                        <li>Con tus <b>{fmt(availableQty)}</b> litros disponibles puedes producir aproximadamente:
                                                            <ul className="mb-0">
                                                                <li><b>{fmt(approxBottlesByLiters.toLocaleString())}</b> botellas</li>
                                                                <li><b>{fmt(approxBoxesByLiters.toLocaleString())}</b> cajas (12 botellas)</li>
                                                            </ul>
                                                        </li>
                                                    </ul>
                                                    <div className="small text-muted">Este cálculo es informativo; puedes capturar cualquier cantidad ≤ a tus litros disponibles.</div>
                                                </InfoNote>
                                            </Col>
                                        </Row>

                                        <Row className="mb-2">
                                            {/* Nombre del producto */}
                                            <Col md={6}>
                                                <Form.Label>Nombre del producto final</Form.Label>
                                                <Form.Control
                                                    value={form.finalName}
                                                    onChange={(e) => setForm(f => ({ ...f, finalName: e.target.value }))}
                                                    placeholder="Ej.: Mezcal joven 750 ml"
                                                />
                                            </Col>

                                            {/* Ubicación destino */}
                                            <Col md={6}>
                                                <Form.Label>Ubicación destino</Form.Label>
                                                <Form.Select
                                                    value={form.destLocationId}
                                                    onChange={(e) => setForm(f => ({ ...f, destLocationId: e.target.value }))}
                                                >
                                                    <option value="">Selecciona ubicación…</option>
                                                    {locations.map(loc => (
                                                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                        </Row>

                                        <Row className="mb-2">
                                            <Col md={6}>
                                                <Form.Label as={RequiredLabel} required>Destino final</Form.Label>
                                                <Form.Control
                                                    value={form.finalDescription}
                                                    onChange={(e) => setForm(f => ({ ...f, finalDescription: e.target.value }))}
                                                    placeholder="Ej.: Botella 750 ml"
                                                />
                                            </Col>

                                            <Col md={6}>
                                                <Form.Label as={RequiredLabel} required>Precio final de venta</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={form.finalPrice}
                                                    onChange={(e) => setForm(f => ({ ...f, finalPrice: e.target.value }))}
                                                    placeholder="Ej.: 1250.00"
                                                />
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            {/* Presentación */}
                                            <Col md={6}>
                                                <Form.Label>Presentación</Form.Label>
                                                <div className="d-flex gap-3 align-items-center">
                                                    <Form.Check
                                                        type="radio" label="Botella 750 ml"
                                                        name="presentation"
                                                        checked={form.presentation === "BOTELLA_750"}
                                                        onChange={() =>
                                                            setForm({ ...form, presentation: "BOTELLA_750", finalUnit: "Botellas", finalCount: "" })
                                                        }
                                                    />
                                                    <Form.Check
                                                        type="radio" label="Caja (12 botellas)"
                                                        name="presentation"
                                                        checked={form.presentation === "CAJA_12"}
                                                        onChange={() =>
                                                            setForm({ ...form, presentation: "CAJA_12", finalUnit: "Cajas", finalCount: "" })
                                                        }
                                                    />
                                                </div>
                                            </Col>

                                            {/* Categoría */}
                                            <Col xl={6}>
                                                <Form.Group>
                                                    <Form.Label as={RequiredLabel} required>Categoría</Form.Label>
                                                    <Form.Control value="Producto Terminado" disabled readOnly />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row className="mb-2">
                                            {/* Presentación del producto final */}
                                            <Col md={6}>
                                                <Form.Label>
                                                    {form.presentation === "BOTELLA_750" ? "Cantidad de botellas" : "Cantidad de cajas"}
                                                </Form.Label>
                                                <Form.Control
                                                    type="number" min="0" step="1"
                                                    value={form.finalCount}
                                                    onChange={e => setForm({ ...form, finalCount: e.target.value })}
                                                    placeholder="0"
                                                />
                                            </Col>

                                            {/* Tabla de equivalencias */}
                                            <Col md={6}>
                                                <Form.Label>Equivalencia aprox.</Form.Label>
                                                <Form.Control
                                                    readOnly
                                                    value={
                                                        !form.finalCount ? "" :
                                                            (form.presentation === "BOTELLA_750"
                                                                ? `${Number(form.finalCount) * litersPerBottle} litros`
                                                                : `${Number(form.finalCount) * bottlesPerBox} botellas = ${Number(form.finalCount) * litersPerBox} L`
                                                            )
                                                    }
                                                    placeholder="—"
                                                />
                                                <Form.Text className="text-muted">
                                                    {form.finalCount
                                                        ? (() => {
                                                            const count = Number(form.finalCount);
                                                            const needed = form.presentation === "BOTELLA_750"
                                                                ? count * litersPerBottle
                                                                : count * litersPerBox;
                                                            const remaining = availableQty - needed;
                                                            return remaining >= 0
                                                                ? `Consumirá ~${needed} litros. Restarán ~${remaining} litros.`
                                                                : `Necesita ~${needed} litros (excede por ${Math.abs(remaining)} litros).`;
                                                        })()
                                                        : "Solo informativo; se registra la unidad elegida."
                                                    }
                                                </Form.Text>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 gt-3">
                                            {/* Consumibles */}
                                            <Col md={12}>
                                                <Form.Label>Claves (consumibles)</Form.Label>
                                                <Form.Select
                                                    multiple
                                                    style={{ height: 110 }} // ajusta a gusto
                                                    value={selectedSupplyIds.map(String)}
                                                    onChange={(e) => {
                                                        const vals = Array.from(e.target.selectedOptions).map(o => Number(o.value));
                                                        setSelectedSupplyIds(vals);
                                                    }}
                                                >
                                                    {supplies.map(s => (
                                                        <option key={s.id} value={s.id}>
                                                            {s.code} - {s.product_name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Text className="text-muted">
                                                    Opcional. Puedes seleccionar una o varias claves consumidas en este lote.
                                                </Form.Text>
                                            </Col>
                                        </Row>

                                        {/* --- ASIGNACIÓN DE FOLIOS --- */}
                                        <Row className="mb-2">
                                            <Col md={12}>
                                                <Form.Label>Asignación de folios</Form.Label>
                                                <div className="d-flex gap-3 flex-wrap">
                                                    {isSinglePiece ? (
                                                        <Form.Check
                                                            type="radio"
                                                            label="Individual"
                                                            checked={true}
                                                            readOnly
                                                        />
                                                    ) : (
                                                        <>
                                                            <Form.Check
                                                                type="radio"
                                                                label="Auto (consecutivos)"
                                                                checked={folioMode === "auto"}
                                                                onChange={() => {
                                                                    setFolioMode("auto");
                                                                    setManualType("range");
                                                                    setForm((f) => ({
                                                                        ...f,
                                                                        folioId: "",
                                                                        startFolioId: "",
                                                                        endFolioId: "",
                                                                    }));
                                                                }}
                                                            />

                                                            <Form.Check
                                                                type="radio"
                                                                label="Manual"
                                                                checked={folioMode === "manual"}
                                                                onChange={() => {
                                                                    setFolioMode("manual");
                                                                    setManualType("range");
                                                                    setForm((f) => ({
                                                                        ...f,
                                                                        folioId: "",
                                                                    }));
                                                                }}
                                                            />

                                                            {folioMode === "manual" && (
                                                                <Form.Check
                                                                    type="radio"
                                                                    label={`Rango (${Number(form.finalCount || 0)} folios)`}
                                                                    checked={manualType === "range"}
                                                                    readOnly
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                <Form.Text className="text-muted">
                                                    {isSinglePiece
                                                        ? "Como se generará una sola pieza, debes asignar un folio individual."
                                                        : folioMode === "auto"
                                                            ? "Se asignarán automáticamente los siguientes folios consecutivos disponibles."
                                                            : "Selecciona el rango de folios a usar para este lote."}
                                                </Form.Text>
                                            </Col>
                                        </Row>

                                        {/* Una pieza */}
                                        {isSinglePiece && (
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <Form.Label>Buscar folio *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Ej. Nn-3749302437"
                                                        value={form.folioSearch || ""}
                                                        onChange={async (e) => {
                                                            const q = e.target.value;
                                                            setForm((f) => ({ ...f, folioSearch: q, folioId: "" }));

                                                            if (q.length >= 4) {
                                                                const resp = await getFoliosByQuery(q, true);
                                                                setFolios(resp?.items || []);
                                                            } else {
                                                                const resp = await getFolios(1, 100, true);
                                                                setFolios(resp?.items || []);
                                                            }
                                                        }}
                                                    />
                                                </Col>

                                                <Col md={6}>
                                                    <Form.Label>Resultado *</Form.Label>
                                                    <Form.Select
                                                        value={form.folioId || ""}
                                                        onChange={(e) =>
                                                            setForm((f) => ({ ...f, folioId: e.target.value }))
                                                        }
                                                    >
                                                        <option value="">Selecciona el folio...</option>
                                                        {folios.map((folio) => (
                                                            <option key={folio.id} value={folio.id}>
                                                                {folio.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                            </Row>
                                        )}

                                        {/* Multiples piezas - consecutivo */}
                                        {isMultiplePieces && form.folioMode === "auto" && (
                                            <Alert variant="info" className="mt-2">
                                                Se asignarán automáticamente los siguientes folios consecutivos disponibles.
                                            </Alert>
                                        )}

                                        {/* Multiples piezas manual */}
                                        {isMultiplePieces && folioMode === "manual" && (
                                            <Row className="g-3 mt-1">
                                                <Col md={6}>
                                                    <Form.Label>Folio inicial *</Form.Label>
                                                    <Form.Select
                                                        value={form.startFolioId || ""}
                                                        onChange={(e) =>
                                                            setForm((f) => ({ ...f, startFolioId: e.target.value }))
                                                        }
                                                    >
                                                        <option value="">Selecciona folio inicial...</option>
                                                        {folios.map((folio) => (
                                                            <option key={folio.id} value={folio.id}>
                                                                {folio.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>

                                                <Col md={6}>
                                                    <Form.Label>Folio final *</Form.Label>
                                                    <Form.Select
                                                        value={form.endFolioId || ""}
                                                        onChange={(e) =>
                                                            setForm((f) => ({ ...f, endFolioId: e.target.value }))
                                                        }
                                                    >
                                                        <option value="">Selecciona folio final...</option>
                                                        {folios.map((folio) => (
                                                            <option key={folio.id} value={folio.id}>
                                                                {folio.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                            </Row>
                                        )}
                                    </>
                                )}
                            </Form>
                        )}

                        {isFinished && (
                            <Alert variant="warning" className="mt-3">
                                Este ítem ya está en <strong>Producto terminado</strong>. No hay acciones disponibles.
                            </Alert>
                        )}

                        {error && <Alert className="mt-3" variant="danger">{error}</Alert>}
                        {okMsg && <Alert className="mt-3" variant="success">{okMsg}</Alert>}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="secondary" onClick={onClose}>Cerrar</Button>
                {(!isFinished) && (
                    <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
                        {saving ? "Guardando..." : "Guardar movimiento"}
                    </Button>
                )}
            </Modal.Footer>

            <ConfirmModal
                show={showBottleCalc}
                onHide={() => setShowBottleCalc(false)}
                denyText={null}                 // para ocultar botón “No”
                title="Calcular factor"
                message="¿Botellas objetivo de 750 ml?"
                confirmText="Calcular"
                confirmVariant="primary"
                onConfirm={() => {
                    const kg = Number(form.quantity);
                    const bottles = Number(bottlesGoal);

                    if (!kg || kg <= 0) {
                        toast.warning("Cantidad origen inválida.");
                        return;
                    }
                    if (!Number.isFinite(bottles) || bottles <= 0) {
                        toast.warning("Captura un número de botellas válido.");
                        return;
                    }

                    const liters = bottles * 0.75;
                    const factor = liters / kg;

                    setForm(f => ({
                        ...f,
                        advanceMode: "factor",
                        advanceFactor: factor.toFixed(6),
                    }));

                    setShowBottleCalc(false);
                }}
            >
                <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    value={bottlesGoal}
                    onChange={(e) => setBottlesGoal(e.target.value)}
                />
            </ConfirmModal>
        </Modal>
    );
}